




































































import { defineComponent, onMounted, reactive } from '@vue/composition-api';
import OlbButton from '../button/button.vue';
import OlbDialog from '../dialog/dialog.vue';
import OlbAmount from '../amount/amount.vue';
import OlbLoader from '../loader/loader.vue';

type TFundInputs = 'initialAmount' | 'monthlyRate' | 'years' | 'averageReturn';
type TFundResults = 'initialAmount' | 'sumOfMonthlySavings' | 'interest' | 'totalSavings';

// eslint-disable-next-line @typescript-eslint/interface-name-prefix
export interface IFundCalculatorRow {
	name: string;
	inputName: TFundInputs;
	inputValue: string;
	inputUnit: string;
}

// eslint-disable-next-line @typescript-eslint/interface-name-prefix
export interface IFundResultRow {
	name: string;
	isResultRow: boolean;
	state: TFundResults;
}

// eslint-disable-next-line @typescript-eslint/interface-name-prefix
interface IFundState {
	inputs: {
		initialAmount: number;
		monthlyRate: number;
		years: number;
		averageReturn: number;
	};
	computed: {
		initialAmount: number;
		sumOfMonthlySavings: number;
		interest: number;
		totalSavings: number;
	};
}

/**
 * Reusable fund component.
 */
export default defineComponent({
	props: {
		titleCalculator: String,
		titleResult: String,
		button: String,
		showButton: Boolean,
		openDialog: String,
		disclaimer: String,
		initialAmount: String,
		initialAmountLabel: String,
		monthlyRate: String,
		monthlyRateLabel: String,
		years: String,
		yearsLabel: String,
		averageReturn: String,
		averageReturnLabel: String,
		initialAmountResult: String,
		sumOfMonthlySavingsResult: String,
		interestResult: String,
		totalSavingsResult: String,
	},
	components: {
		OlbAmount,
		OlbButton,
		OlbDialog,
		OlbLoader,
	},
	setup(props, { root }) {
		const calculatorRows: IFundCalculatorRow[] = [
			{
				name: props.initialAmountLabel || 'Einmaliger Anlagebetrag',
				inputName: 'initialAmount',
				inputValue: props.initialAmount || '10000',
				inputUnit: '€',
			},
			{
				name: props.monthlyRateLabel || 'Monatliche Sparrate',
				inputName: 'monthlyRate',
				inputValue: props.monthlyRate || '150',
				inputUnit: '€',
			},
			{
				name: props.yearsLabel || 'Anlagedauer',
				inputName: 'years',
				inputValue: props.years || '10',
				inputUnit: 'JAHRE',
			},
			{
				name: props.averageReturnLabel || 'Durchschnittliche Jahresrendite',
				inputName: 'averageReturn',
				inputValue: props.averageReturn || '6,0',
				inputUnit: '%',
			},
		];

		const resultRows: IFundResultRow[] = [
			{
				name: props.initialAmountResult || 'Einmaliger Anlagebetrag',
				isResultRow: false,
				state: 'initialAmount',
			},
			{
				name: props.sumOfMonthlySavingsResult || 'Summe monatlicher Sparraten',
				isResultRow: false,
				state: 'sumOfMonthlySavings',
			},
			{
				name: props.interestResult || 'Rendite',
				isResultRow: false,
				state: 'interest',
			},
			{
				name: props.totalSavingsResult || 'Gesamtsparleistung',
				isResultRow: true,
				state: 'totalSavings',
			},
		];
		const state: IFundState = reactive({
			inputs: {
				initialAmount: 0,
				monthlyRate: 0,
				years: 0,
				averageReturn: 0,
			},
			computed: {
				initialAmount: 0,
				sumOfMonthlySavings: 0,
				interest: 0,
				totalSavings: 0,
			},
		});

		function calcSumOfMonthlySavings(monthlyRate: number, years: number) {
			return monthlyRate * years * 12;
		}

		function calcInterest(averageReturn: number) {
			return 1 + averageReturn / 100;
		}

		function calcInterestFromInitialAmount(initialAmount: number, years: number, interest: number) {
			return initialAmount * Math.pow(interest, years);
		}

		function calcInterestFromMonthlySavings(monthlyRate: number, years: number, interest: number) {
			return monthlyRate * 12 * ((Math.pow(interest, years) - 1) / (interest - 1));
		}

		function calcTotalSavings(state: IFundState) {
			const { initialAmount, monthlyRate, years, averageReturn } = state.inputs;
			const hasInitialAmount = !isNaN(initialAmount) && initialAmount > 0;
			const hasMonthlyRate = !isNaN(monthlyRate) && monthlyRate > 0;
			const hasYears = !isNaN(years) && years > 0;
			const hasAverageReturn = !isNaN(averageReturn) && averageReturn > 0;

			if (hasInitialAmount && hasMonthlyRate && hasYears && hasAverageReturn) {
				return (
					calcInterestFromInitialAmount(initialAmount, years, calcInterest(averageReturn)) +
					calcInterestFromMonthlySavings(monthlyRate, years, calcInterest(averageReturn))
				);
			}
			if (hasInitialAmount && hasYears && hasAverageReturn) {
				return calcInterestFromInitialAmount(initialAmount, years, calcInterest(averageReturn));
			}
			if (hasInitialAmount && hasMonthlyRate && hasYears) {
				return initialAmount + calcSumOfMonthlySavings(monthlyRate, years);
			}
			if (hasMonthlyRate && hasYears && hasAverageReturn) {
				return calcInterestFromMonthlySavings(monthlyRate, years, calcInterest(averageReturn));
			}
			if (hasMonthlyRate && hasYears) {
				return calcSumOfMonthlySavings(monthlyRate, years);
			}
			if (hasInitialAmount) {
				return initialAmount;
			}
			return 0;
		}

		/**
		 * Calculate the different results based on the current inputs
		 */
		function calcComputedValues() {
			const { monthlyRate, years } = state.inputs;
			state.computed.initialAmount = state.inputs.initialAmount;
			state.computed.sumOfMonthlySavings = calcSumOfMonthlySavings(monthlyRate, years);
			state.computed.totalSavings = calcTotalSavings(state);
			state.computed.interest =
				state.computed.totalSavings - (state.computed.initialAmount + state.computed.sumOfMonthlySavings);
		}

		/**
		 * Transform value to number with 2 decimals and thousand separator
		 */
		function toDecimal(value: number) {
			return value.toLocaleString('de', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
		}

		/**
		 * Store value of input field in state
		 */
		function onInput(e: KeyboardEvent) {
			if (!e.isTrusted) {
				return;
			}
			const target = e.target as HTMLInputElement;
			const name = target.name as TFundInputs;
			const value = target.value.replaceAll('.', '').replace(',', '.');
			const numberVal = isNaN(parseFloat(value)) ? 0 : parseFloat(value);
			state.inputs[name] = numberVal;
			calcComputedValues();
		}

		/**
		 * Set input value to 0 when left blank by user
		 */
		function onBlur(e: KeyboardEvent) {
			const target = e.target as HTMLInputElement;
			const name = target.name as TFundInputs;
			if (state.inputs[name] === 0) {
				target.value = '0';
			}
		}

		function onClick() {
			if (!props.openDialog) {
				return;
			}
			// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
			//@ts-ignore
			root.$refs[props.openDialog] && root.$refs[props.openDialog]?.toggleDialog();
		}

		/**
		 * Calculate the results for the initial input values
		 */
		onMounted(() => {
			calculatorRows.forEach((row) => {
				state.inputs[row.inputName] = parseFloat(row.inputValue);
			});
			calcComputedValues();
		});

		return { state, toDecimal, onInput, onBlur, onClick, calculatorRows, resultRows };
	},
});
